import {
  HttpInterceptorFn,
  HttpErrorResponse,
  HttpRequest,
  HttpHandlerFn,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, switchMap, throwError, Subject, filter, take } from 'rxjs';
import { AuthService } from './auth.service';

/**
 * Tracks whether a token refresh is currently in progress to prevent
 * multiple concurrent refresh calls (thundering herd problem).
 */
let isRefreshing = false;

/**
 * Subject used to queue failed requests while a refresh is in progress.
 * Emits `true` when refresh succeeds (retry queued requests),
 * `false` when refresh fails (reject queued requests).
 */
let refreshSubject = new Subject<boolean>();

/**
 * Interceptor that handles 401 Unauthorized responses by:
 * 1. Calling the refresh token endpoint to get new cookies
 * 2. Retrying the original failed request
 * 3. If refresh also fails, redirecting to /login
 *
 * Skips auth endpoints (/account/login, /account/refresh) to prevent infinite loops.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  // Skip interception for auth endpoints to prevent infinite refresh loops
  if (isAuthEndpoint(req.url)) {
    return next(req);
  }

  return next(req).pipe(
    catchError((error: unknown) => {
      if (!(error instanceof HttpErrorResponse) || error.status !== 401) {
        return throwError(() => error);
      }

      return handle401(req, next, authService, router);
    }),
  );
};

function isAuthEndpoint(url: string): boolean {
  return url.includes('/account/login') || url.includes('/account/refresh');
}

function handle401(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  authService: AuthService,
  router: Router,
) {
  // If a refresh is already in progress, queue this request
  if (isRefreshing) {
    return refreshSubject.pipe(
      filter((success) => success),
      take(1),
      switchMap(() => next(req)),
    );
  }

  // Start a new refresh
  isRefreshing = true;
  refreshSubject = new Subject<boolean>();

  return authService.refreshToken().pipe(
    switchMap(() => {
      isRefreshing = false;
      refreshSubject.next(true);
      refreshSubject.complete();
      return next(req);
    }),
    catchError((refreshError: unknown) => {
      isRefreshing = false;
      refreshSubject.next(false);
      refreshSubject.complete();
      authService.logout();
      router.navigate(['/login']);
      return throwError(() => refreshError);
    }),
  );
}
